import { secondsToMilliseconds } from "@suse-o11y/date-time";
import type { CombinedDateTimeFormatWithTime } from "@suse-o11y/date-time/format/combined-date-time-format";
import { formatViewInterval } from "@suse-o11y/date-time/format/format-view-interval";
import type { OpenInterval } from "@suse-o11y/date-time/open-interval";
import { useNow } from "@suse-o11y/use-now";
import { useSelector } from "react-redux";

import { selectTimestampDate } from "../../../app/state/view/timeline/TimelineSelectors";

/**
 * Takes an interval and returns a formatted interval that also takes the current view time into account.
 * If the user is not on a view then the formatting will behave like `formatInterval`.
 *
 * Formatting may be based on relative formatting. For example:
 *  - when the interval is on the same day, the day and month are left out,
 *  - and if the interval is on the same year the year is left out.
 * When this formatting is no longer valid, because of the march of time, the formatting
 * will automatically update.
 */
export const useFormattedViewInterval = (interval: OpenInterval): CombinedDateTimeFormatWithTime => {
	const viewTime = useSelector(selectTimestampDate);

	return useNow({
		interval: secondsToMilliseconds(1),
		floor: "milliseconds",
		transform: (now) => formatViewInterval({ interval, viewTime: viewTime ?? now, now }),
		compare: (prev, next) => prev.toString() === next.toString(),
	});
};
